import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe,
  Mail,
  MessageSquare,
  Bell,
  Database,
  Shield,
  Users,
  FileText,
  Palette,
  Zap,
  ArrowRight,
  Loader2,
} from 'lucide-react';
// import LoadingSpinner from '../components/LoadingSpinner'; // Unused import

interface SettingsCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  href: string;
  status: 'configured' | 'needs-attention' | 'not-configured';
  itemCount?: number;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState<string | null>(null);
  const [categories, _setCategories] = useState<SettingsCategory[]>([
    {
      id: 'system',
      name: 'System Configuration',
      description:
        'Configure global system preferences including timezone, currency, language, and regional settings that affect all users',
      icon: Globe,
      href: '/dashboard/settings/system',
      status: 'configured',
    },
    {
      id: 'email-templates',
      name: 'Email Templates',
      description:
        'Design professional email templates with variables, WYSIWYG editing, and automated delivery for all system communications',
      icon: Mail,
      href: '/dashboard/settings/templates/email',
      status: 'needs-attention',
      itemCount: 12,
    },
    {
      id: 'sms-templates',
      name: 'SMS Templates',
      description: 'Manage SMS message templates for alerts and notifications',
      icon: MessageSquare,
      href: '/dashboard/settings/templates/sms',
      status: 'configured',
      itemCount: 8,
    },
    {
      id: 'notification-templates',
      name: 'Notification Templates',
      description: 'Configure in-app notification templates and delivery settings',
      icon: Bell,
      href: '/dashboard/settings/templates/notifications',
      status: 'configured',
      itemCount: 15,
    },
    {
      id: 'dropdowns',
      name: 'Dropdown Management',
      description: 'Manage system dropdown options, categories, and configuration sets',
      icon: Database,
      href: '/dashboard/settings/dropdowns',
      status: 'configured',
      itemCount: 24,
    },
    {
      id: 'security',
      name: 'Security & Authentication',
      description: 'Configure security policies, authentication methods, and access controls',
      icon: Shield,
      href: '/dashboard/settings/security',
      status: 'needs-attention',
    },
    {
      id: 'user-defaults',
      name: 'User Defaults',
      description: 'Set default preferences, roles, and permissions for new users',
      icon: Users,
      href: '/dashboard/settings/user-defaults',
      status: 'configured',
    },
    {
      id: 'branding',
      name: 'Branding & Appearance',
      description: 'Customize logos, colors, themes, and overall application appearance',
      icon: Palette,
      href: '/dashboard/settings/branding',
      status: 'not-configured',
    },
    {
      id: 'integrations',
      name: 'Integrations & APIs',
      description: 'Manage third-party integrations, API keys, and external service connections',
      icon: Zap,
      href: '/dashboard/settings/integrations',
      status: 'configured',
    },
    {
      id: 'documents',
      name: 'Document Settings',
      description: 'Configure document templates, approval workflows, and file management policies',
      icon: FileText,
      href: '/dashboard/settings/documents',
      status: 'configured',
    },
  ]);

  useEffect(() => {
    // Simulate loading time to prevent flickering
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const handleNavigation = async (href: string) => {
    setNavigating(href);
    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 150));
    navigate(href);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-64 mb-8"></div>
          <div className="bg-white border border-gray-200 rounded-lg">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="px-4 py-4 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Settings</h1>
        <p className="text-sm text-gray-600">Manage your system configuration and preferences</p>
      </div>

      {/* Settings Categories */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {categories.map((category, index) => {
          const Icon = category.icon;
          const isNavigating = navigating === category.href;

          return (
            <div key={category.id}>
              <div
                onClick={() => handleNavigation(category.href)}
                className="px-4 py-4 hover:bg-red-50 cursor-pointer flex items-center justify-between group transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <Icon
                      className={`h-5 w-5 transition-colors ${
                        category.status === 'needs-attention'
                          ? 'text-red-500'
                          : 'text-gray-500 group-hover:text-red-500'
                      }`}
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900 group-hover:text-gray-900">
                        {category.name}
                      </h4>

                      {category.status === 'needs-attention' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Needs attention
                        </span>
                      )}

                      {category.status === 'configured' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Configured
                        </span>
                      )}

                      {category.itemCount && (
                        <span className="text-xs text-gray-500">{category.itemCount} items</span>
                      )}
                    </div>

                    <p className="text-xs text-gray-600">{category.description}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {isNavigating && <Loader2 className="h-4 w-4 text-red-500 animate-spin" />}
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors" />
                </div>
              </div>
              {index < categories.length - 1 && <div className="border-t border-gray-100" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Settings;
